import React, { useState, useMemo, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';

export interface LocationInfo {
  cityId: number;
  cityName: string;
  candidateCount: number;
  url: string;
  locationId: string;
}

export interface ConsolidateItem {
  companyId: string;
  name: string;
  winnerUrl: string;
  isWinnerUrlValid: boolean;
  allLocations: LocationInfo[];
  winnerLocationIds: string[];
  loserCompanyIds: string[];
  batchKey: string; // Added to prevent state collisions
}

interface PartitionConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (batchResults: { companyId: string; winnerCompanyId: string; loserCompanyIds: string[]; selectedCityIds: number[]; confirmedUrl: string }[]) => void;
  items: ConsolidateItem[];
  isLoading?: boolean;
}

export default function PartitionConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  items,
  isLoading = false
}: PartitionConfirmModalProps) {
  // Local state for each item in the batch
  const [batchState, setBatchState] = useState<Record<string, {
    confirmedUrl: string;
    selectedAdditionalIds: Set<string>;
  }>>({});

  // Initialize state when items change
  useEffect(() => {
    const newState: Record<string, { confirmedUrl: string; selectedAdditionalIds: Set<string> }> = {};
    items.forEach(item => {
      newState[item.batchKey] = {
        confirmedUrl: item.isWinnerUrlValid ? item.winnerUrl : "",
        selectedAdditionalIds: new Set(item.winnerLocationIds)
      };
    });
    setBatchState(newState);
  }, [items]);

  const toggleOptional = (batchKey: string, locationIds: string[], checked: boolean) => {
    setBatchState(prev => {
      const current = prev[batchKey];
      if (!current) return prev;
      
      const nextAdditional = new Set(current.selectedAdditionalIds);
      locationIds.forEach(id => {
        if (checked) nextAdditional.add(id);
        else nextAdditional.delete(id);
      });

      return {
        ...prev,
        [batchKey]: { ...current, selectedAdditionalIds: nextAdditional }
      };
    });
  };

  const updateUrl = (batchKey: string, url: string) => {
    setBatchState(prev => ({
      ...prev,
      [batchKey]: { ...prev[batchKey], confirmedUrl: url }
    }));
  };

  const handleConfirm = () => {
    const results = items.map(item => {
      const state = batchState[item.batchKey];
      const selectedLocationIds = Array.from(state?.selectedAdditionalIds || []);
      
      // Map locationIds back to cityIds for the API
      const selectedCityIds = item.allLocations
        .filter(l => selectedLocationIds.includes(l.locationId))
        .map(l => l.cityId);

      return {
        companyId: item.companyId,
        winnerCompanyId: item.companyId,
        loserCompanyIds: item.loserCompanyIds,
        selectedCityIds: [...new Set(selectedCityIds)],
        confirmedUrl: state?.confirmedUrl || ""
      };
    });
    onConfirm(results);
  };

  const calculateTotalCandidates = () => {
    let total = 0;
    items.forEach(item => {
      const state = batchState[item.batchKey];
      item.allLocations.forEach(loc => {
        if (state?.selectedAdditionalIds.has(loc.locationId)) {
          total += loc.candidateCount;
        }
      });
    });
    return total;
  };

  const isFormValid = items.every(item => {
    const state = batchState[item.batchKey];
    const hasUrl = state?.confirmedUrl.trim().length > 0;
    const hasLocation = (state?.selectedAdditionalIds?.size || 0) > 0;
    return hasUrl && hasLocation;
  });

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-darkbg text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl flex flex-col max-h-[90vh]">
                <div className="px-4 pb-4 pt-5 sm:p-6 sm:pb-4 border-b border-gray-100 dark:border-gray-800 shrink-0">
                  <div className="flex items-center justify-between">
                    <Dialog.Title as="h3" className="text-xl font-bold leading-6 text-gray-900 dark:text-white">
                      Review & confirm Batch ({items.length} Companies)
                    </Dialog.Title>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Verify URLs and select locations for all selected companies before confirming.
                  </p>
                </div>

                <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-8 bg-gray-50/30 dark:bg-darkbg/30">
                   {items.map((item, idx) => {
                    const state = batchState[item.batchKey];
                    const groupedByCity = (() => {
                      const groups: Record<number, { cityName: string; variants: LocationInfo[]; isMandatory: boolean; cityId: number }> = {};
                      item.allLocations.forEach(loc => {
                        if (!groups[loc.cityId]) {
                          groups[loc.cityId] = { cityName: loc.cityName, variants: [], isMandatory: false, cityId: loc.cityId };
                        }
                        groups[loc.cityId].variants.push(loc);
                        if (item.winnerLocationIds.includes(loc.locationId)) {
                          groups[loc.cityId].isMandatory = true;
                        }
                      });
                      return Object.values(groups);
                    })();

                    return (
                      <div key={item.batchKey} className="bg-white dark:bg-darkbglight rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                          <h4 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            {item.name}
                            {item.loserCompanyIds.length > 0 && (
                              <span className="text-[10px] bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded uppercase tracking-wider">Partition</span>
                            )}
                          </h4>
                        </div>
                        
                        <div className="p-4 space-y-4">
                          <div className="flex flex-col gap-1.5">
                            <label className="text-[10px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">Primary URL</label>
                            <input 
                              type="text"
                              value={state?.confirmedUrl || ""}
                              onChange={(e) => updateUrl(item.batchKey, e.target.value)}
                              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-blue-100 dark:border-blue-700 rounded-md focus:ring-2 focus:ring-entntblue outline-none text-gray-900 dark:text-white font-medium"
                              placeholder="Enter canonical URL"
                            />
                          </div>

                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Locations to Merge</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {groupedByCity.map((group) => {
                                const cityChecked = group.variants.some(v => state?.selectedAdditionalIds.has(v.locationId));
                                const totalCityCandidates = group.variants.reduce((sum, v) => sum + v.candidateCount, 0);

                                return (
                                  <div 
                                    key={group.cityId} 
                                    className={`border rounded-md p-2 transition-all ${
                                      group.isMandatory 
                                        ? 'border-blue-200 bg-blue-50/30 dark:border-blue-900/30' 
                                        : 'border-gray-200 dark:border-gray-700 hover:border-entntblue'
                                    }`}
                                  >
                                    <label className="flex items-center justify-between cursor-pointer">
                                      <div className="flex items-center gap-2 min-w-0">
                                        <input
                                          type="checkbox"
                                          checked={cityChecked}
                                          onChange={() => {
                                            const allVariantIds = group.variants.map(v => v.locationId);
                                            toggleOptional(item.batchKey, allVariantIds, !cityChecked);
                                          }}
                                          className="h-4 w-4 rounded border-gray-300 text-entntblue focus:ring-entntblue transition-all"
                                        />
                                        <span className={`text-xs font-medium truncate ${
                                          group.isMandatory ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-white'
                                        }`}>
                                          {group.cityName}
                                        </span>
                                      </div>
                                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${
                                        group.isMandatory
                                          ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-300 border-blue-100'
                                          : 'bg-gray-50 dark:bg-gray-800 text-gray-500 border-gray-100 dark:border-gray-700'
                                      }`}>
                                        {totalCityCandidates}
                                      </span>
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-800/80 px-4 py-4 sm:px-6 border-t border-gray-200 dark:border-gray-700 shrink-0 sm:flex sm:items-center sm:justify-between">
                  <div className="mb-4 sm:mb-0 text-center sm:text-left">
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      Total: {calculateTotalCandidates()} Candidates
                    </p>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-widest font-bold">
                      Across {items.length} confirmed companies
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      className="flex-1 sm:flex-none rounded-md bg-white dark:bg-darkbg px-4 py-2.5 text-sm font-bold text-gray-700 dark:text-gray-300 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={!isFormValid || isLoading}
                      className={`flex-1 sm:flex-none rounded-md px-6 py-2.5 text-sm font-bold text-white shadow-sm transition-all ${
                        !isFormValid || isLoading
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-entntblue hover:bg-blue-500 shadow-blue-500/20'
                      }`}
                      onClick={handleConfirm}
                    >
                      {isLoading ? (
                        <div className="flex items-center gap-2">
                          <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                          Processing...
                        </div>
                      ) : (
                        `Confirm Batch`
                      )}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
